import React, { useEffect, useState } from "react";
import { Panel, Field } from "@/pages/admin/shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/pages/admin/api";

export default function HomepageEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site-content").then((r) => setData(r.data.homepage || {}));
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const set = (key, value) => setData({ ...data, [key]: value });
  const setFeature = (idx, key, value) => {
    const next = [...(data.features || [])];
    next[idx] = { ...next[idx], [key]: value };
    set("features", next);
  };
  const addFeature = () => set("features", [...(data.features || []), { title: "", description: "", icon: "Sparkles" }]);
  const delFeature = (idx) => set("features", (data.features || []).filter((_, i) => i !== idx));

  const setStat = (idx, key, value) => {
    const next = [...(data.statistics || [])];
    next[idx] = { ...next[idx], [key]: value };
    set("statistics", next);
  };
  const addStat = () => set("statistics", [...(data.statistics || []), { value: "", label: "" }]);
  const delStat = (idx) => set("statistics", (data.statistics || []).filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site-content", { homepage: data });
      toast.success("Homepage updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <Panel title="Hero Section" actions={<Button data-testid="save-homepage" onClick={save} disabled={saving} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white"><Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save"}</Button>}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Hero Title"><Input data-testid="hero-title" value={data.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} /></Field>
          <Field label="Highlighted Word"><Input data-testid="hero-highlight" value={data.hero_highlight || ""} onChange={(e) => set("hero_highlight", e.target.value)} /></Field>
          <Field label="Hero Description" className="md:col-span-2"><Textarea data-testid="hero-desc" rows={3} value={data.hero_description || ""} onChange={(e) => set("hero_description", e.target.value)} /></Field>
          <Field label="CTA Button Text"><Input data-testid="hero-cta" value={data.hero_cta_text || ""} onChange={(e) => set("hero_cta_text", e.target.value)} /></Field>
          <Field label="Hero Image URL"><Input value={data.hero_image || ""} onChange={(e) => set("hero_image", e.target.value)} placeholder="Optional image URL" /></Field>
        </div>
      </Panel>

      <Panel title="Trust Items" subtitle="Comma-separated badges shown under the CTA">
        <Field label="Trust items">
          <Input value={(data.trust_items || []).join(", ")} onChange={(e) => set("trust_items", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
        </Field>
      </Panel>

      <Panel
        title="Feature Cards"
        subtitle={`${(data.features || []).length} cards on 'Why Choose Us' section`}
        actions={<Button variant="outline" onClick={addFeature} className="rounded-xl"><Plus size={14} className="mr-1" /> Add</Button>}
      >
        <Field label="Section heading" className="mb-4"><Input value={data.why_section_heading || ""} onChange={(e) => set("why_section_heading", e.target.value)} /></Field>
        <div className="space-y-3">
          {(data.features || []).map((f, i) => (
            <div key={i} className="grid md:grid-cols-[1fr_2fr_1fr_36px] gap-3 items-end rounded-xl border border-border p-3">
              <Field label="Title"><Input value={f.title} onChange={(e) => setFeature(i, "title", e.target.value)} /></Field>
              <Field label="Description"><Input value={f.description} onChange={(e) => setFeature(i, "description", e.target.value)} /></Field>
              <Field label="Icon" hint="Lucide icon name"><Input value={f.icon} onChange={(e) => setFeature(i, "icon", e.target.value)} /></Field>
              <button onClick={() => delFeature(i)} className="text-red-500 hover:opacity-70 p-2"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Statistics" actions={<Button variant="outline" onClick={addStat} className="rounded-xl"><Plus size={14} className="mr-1" /> Add</Button>}>
        <div className="space-y-3">
          {(data.statistics || []).map((s, i) => (
            <div key={i} className="grid md:grid-cols-[1fr_2fr_36px] gap-3 items-end rounded-xl border border-border p-3">
              <Field label="Value"><Input value={s.value} onChange={(e) => setStat(i, "value", e.target.value)} /></Field>
              <Field label="Label"><Input value={s.label} onChange={(e) => setStat(i, "label", e.target.value)} /></Field>
              <button onClick={() => delStat(i)} className="text-red-500 hover:opacity-70 p-2"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Panel>

      <div className="flex justify-end">
        <Button data-testid="save-homepage-bottom" onClick={save} disabled={saving} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white"><Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save all changes"}</Button>
      </div>
    </div>
  );
}
